import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'system' | 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class DarkModeService {
  private readonly DARK_MODE_KEY = 'darkMode';
  private readonly THEME_MODE_KEY = 'themeMode';
  private readonly DARK_STATUS_BAR_COLOR = '#1a1a1a';
  private readonly LIGHT_STATUS_BAR_COLOR = '#ffffff';
  private readonly DARK_NAVIGATION_BAR_COLOR = '#1a1a1a';
  private readonly LIGHT_NAVIGATION_BAR_COLOR = '#ffffff';
  private readonly LIGHT_NAVIGATION_BAR_DIVIDER_COLOR = '#e5e7eb';
  private systemThemeQuery?: MediaQueryList;
  private darkModeSubject = new BehaviorSubject<boolean>(this.getResolvedDarkMode());
  private themeModeSubject = new BehaviorSubject<ThemeMode>(this.getInitialThemeMode());
  
  public darkMode$: Observable<boolean> = this.darkModeSubject.asObservable();
  public themeMode$: Observable<ThemeMode> = this.themeModeSubject.asObservable();

  constructor() {
    this.applyDarkMode(this.isDarkMode());
    this.listenToSystemThemeChanges();
  }

  /**
   * Get the initial theme preference from localStorage.
   */
  private getInitialThemeMode(): ThemeMode {
    const savedThemeMode = localStorage.getItem(this.THEME_MODE_KEY);

    if (
      savedThemeMode === 'system' ||
      savedThemeMode === 'light' ||
      savedThemeMode === 'dark'
    ) {
      return savedThemeMode;
    }

    const savedMode = localStorage.getItem(this.DARK_MODE_KEY);

    if (savedMode !== null) {
      return JSON.parse(savedMode) ? 'dark' : 'light';
    }

    return 'system';
  }

  private getResolvedDarkMode(mode: ThemeMode = this.getInitialThemeMode()): boolean {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;

    return this.getSystemPrefersDark();
  }

  private getSystemPrefersDark(): boolean {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    );
  }

  /**
   * Check if dark mode is currently enabled
   */
  isDarkMode(): boolean {
    return this.darkModeSubject.value;
  }

  getThemeMode(): ThemeMode {
    return this.themeModeSubject.value;
  }

  /**
   * Toggle dark mode on/off
   */
  toggleDarkMode(): void {
    const newMode = !this.isDarkMode();
    this.setDarkMode(newMode);
  }

  /**
   * Set dark mode to a specific state
   */
  setDarkMode(enabled: boolean): void {
    this.setThemeMode(enabled ? 'dark' : 'light');
  }

  setThemeMode(mode: ThemeMode): void {
    const enabled = this.getResolvedDarkMode(mode);

    this.themeModeSubject.next(mode);
    this.darkModeSubject.next(enabled);
    this.applyDarkMode(enabled);

    localStorage.setItem(this.THEME_MODE_KEY, mode);
    localStorage.setItem(this.DARK_MODE_KEY, JSON.stringify(enabled));
  }

  /**
   * Apply dark mode styles to the document
   */
  private applyDarkMode(enabled: boolean): void {
    const body = document.body;
    const root = document.documentElement;
    
    if (enabled) {
      root.classList.add('dark');
      root.classList.add('ion-palette-dark');
      root.classList.remove('light');
      body.classList.add('dark');
      body.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
      root.classList.remove('ion-palette-dark');
      body.classList.add('light');
      body.classList.remove('dark');
    }

    // Update meta theme-color for mobile browsers
    this.updateThemeColor(enabled);
    this.updateColorScheme(enabled);
    
    // Update native system bars for mobile apps
    void this.updateSystemBarsStyle(enabled);
  }

  /**
   * Update the theme-color meta tag for mobile browsers
   */
  private updateThemeColor(isDark: boolean): void {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }

    const color = isDark ? '#1a1a1a' : '#ffffff';
    metaThemeColor.setAttribute('content', color);
  }

  /**
   * Keep browser-native controls aligned with the selected app theme.
   */
  private updateColorScheme(isDark: boolean): void {
    let metaColorScheme = document.querySelector('meta[name="color-scheme"]');

    if (!metaColorScheme) {
      metaColorScheme = document.createElement('meta');
      metaColorScheme.setAttribute('name', 'color-scheme');
      document.head.appendChild(metaColorScheme);
    }

    metaColorScheme.setAttribute('content', isDark ? 'dark' : 'light');
  }

  /**
   * Update status bar style for mobile apps (Capacitor)
   */
  public async updateStatusBarStyle(isDark = this.isDarkMode()): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({
        color: isDark ? this.DARK_STATUS_BAR_COLOR : this.LIGHT_STATUS_BAR_COLOR,
      });
      await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    } catch (err) {
      console.warn('StatusBar update failed', err);
    }
  }

  /**
   * Update Android navigation bar color and button contrast.
   */
  public async updateNavigationBarStyle(isDark = this.isDarkMode()): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    try {
      await NavigationBar.setNavigationBarColor({
        color: isDark
          ? this.DARK_NAVIGATION_BAR_COLOR
          : this.LIGHT_NAVIGATION_BAR_COLOR,
        darkButtons: !isDark,
        dividerColor: isDark
          ? this.DARK_NAVIGATION_BAR_COLOR
          : this.LIGHT_NAVIGATION_BAR_DIVIDER_COLOR,
      });
    } catch (err) {
      console.warn('NavigationBar update failed', err);
    }
  }

  public async updateSystemBarsStyle(isDark = this.isDarkMode()): Promise<void> {
    await Promise.all([
      this.updateStatusBarStyle(isDark),
      this.updateNavigationBarStyle(isDark),
    ]);
  }

  /**
   * Listen to system theme changes
   */
  listenToSystemThemeChanges(): void {
    if (window.matchMedia && !this.systemThemeQuery) {
      this.systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      this.systemThemeQuery.addEventListener('change', (e) => {
        if (this.getThemeMode() === 'system') {
          this.darkModeSubject.next(e.matches);
          this.applyDarkMode(e.matches);
        }
      });
    }
  }

  /**
   * Reset to system preference
   */
  resetToSystemPreference(): void {
    localStorage.removeItem(this.DARK_MODE_KEY);
    this.setThemeMode('system');
  }
}
